import {
	CODE_COLOURS,
	CODE_LENGTH,
	type CodeBreakerState,
	FLEET_SIZE,
	FLIP_SIZE,
	type FleetState,
	type FlipFourState,
	flipsFor,
	PITS_PER_SIDE,
	type PitRowState,
	type PlayerIndex,
	SLIDE_SIZE,
	type SlidingState,
	STORE,
} from "@playpal/core";
import { useState } from "react";
import { Button } from "../components/ui";

interface BoardProps<S> {
	state: S;
	seat: PlayerIndex;
	onMove: (move: unknown) => void;
}

// --- Fleet Battle ---------------------------------------------------------

export function FleetBoard({ state, seat, onMove }: BoardProps<FleetState>) {
	const myTurn = state.turn === seat && !state.result;
	const them: PlayerIndex = seat === 0 ? 1 : 0;
	const myShipCells = new Set(state.ships[seat].flat());
	const theirSunkCells = new Set(state.ships[them].flat());

	return (
		<div className="board-frame board-lg board-duo">
			<p className="mb-2 text-center text-lg font-semibold">
				{myTurn ? "Fire at their sea!" : "They're taking a shot…"}
			</p>

			{/* Side by side from a tablet up: two seas stacked are taller than the
			    screen at any size, so this is the one board that has to turn. */}
			<div className="md:grid md:grid-cols-2 md:gap-4">
				<div>
					<p className="mb-1 text-sm font-bold opacity-70">Their sea</p>
					<div
						className="mb-4 grid gap-1 rounded-2xl bg-sky-200/60 p-2 md:mb-0 dark:bg-sky-900/40"
						style={{ gridTemplateColumns: `repeat(${FLEET_SIZE}, minmax(0, 1fr))` }}
					>
						{Array.from({ length: FLEET_SIZE * FLEET_SIZE }, (_, cell) => {
							const fired = state.shots[seat].includes(cell);
							const hit = state.hits[seat].includes(cell);
							const sunk = hit && theirSunkCells.has(cell);
							return (
								<button
									key={cell}
									type="button"
									disabled={!myTurn || fired}
									aria-label={`Fire at row ${Math.floor(cell / FLEET_SIZE) + 1}, column ${
										(cell % FLEET_SIZE) + 1
									}${fired ? (hit ? ", hit" : ", missed") : ""}`}
									onClick={() => onMove({ cell })}
									className={`flex aspect-square items-center justify-center rounded-lg text-xl transition ${
										sunk
											? "bg-rose-600"
											: hit
												? "bg-rose-400"
												: fired
													? "bg-sky-100 dark:bg-sky-950"
													: "bg-sky-400/70 enabled:hover:bg-sky-300"
									}`}
								>
									{hit ? "💥" : fired ? "🌊" : ""}
								</button>
							);
						})}
					</div>
				</div>
				<div>
					<p className="mb-1 text-sm font-bold opacity-70">Your sea</p>
					<div
						className="grid gap-1 rounded-2xl bg-indigo-200/60 p-2 dark:bg-indigo-900/40"
						style={{ gridTemplateColumns: `repeat(${FLEET_SIZE}, minmax(0, 1fr))` }}
					>
						{Array.from({ length: FLEET_SIZE * FLEET_SIZE }, (_, cell) => {
							const ship = myShipCells.has(cell);
							const struck = state.hits[them].includes(cell);
							const missed = state.shots[them].includes(cell) && !struck;
							return (
								<span
									key={cell}
									className={`flex aspect-square items-center justify-center rounded-lg text-lg ${
										struck ? "bg-rose-500" : ship ? "bg-indigo-500" : "bg-white/60 dark:bg-white/10"
									}`}
								>
									{struck ? "💥" : missed ? "·" : ship ? "🚢" : ""}
								</span>
							);
						})}
					</div>
				</div>
			</div>

			{state.lastShot && (
				<p className="mt-3 text-center font-semibold">
					{state.lastShot.seat === seat ? "You" : "They"}{" "}
					{state.lastShot.sunk
						? "sank a ship! 🎉"
						: state.lastShot.hit
							? "scored a hit!"
							: "missed."}
				</p>
			)}
		</div>
	);
}

// --- Flip Four ------------------------------------------------------------

export function FlipFourBoard({ state, seat, onMove }: BoardProps<FlipFourState>) {
	const myTurn = state.turn === seat && !state.result;

	return (
		<div className="board-frame board-lg">
			<p className="mb-2 text-center text-lg font-semibold">
				⚫ {state.scores[0]} — {state.scores[1]} ⚪{state.passed && " · someone had to miss a go"}
			</p>
			<div
				className="grid gap-0.5 rounded-2xl bg-emerald-700 p-2 shadow-xl sm:gap-1"
				style={{ gridTemplateColumns: `repeat(${FLIP_SIZE}, minmax(0, 1fr))` }}
			>
				{state.board.map((cell, square) => {
					const playable = myTurn && flipsFor(state.board, square, seat).length > 0;
					return (
						<button
							key={square}
							type="button"
							disabled={!playable}
							aria-label={`Row ${Math.floor(square / FLIP_SIZE) + 1}, column ${
								(square % FLIP_SIZE) + 1
							}${cell === null ? ", empty" : ""}`}
							onClick={() => onMove({ square })}
							className={`flex aspect-square items-center justify-center rounded-md bg-emerald-600 transition ${
								playable ? "ring-2 ring-amber-300 hover:bg-emerald-500" : ""
							}`}
						>
							{cell !== null && (
								// An Othello disc is one piece with a dark side and a light
								// side, and turning it over is the whole game. It used to pop
								// in and out of existence in the new colour instead.
								<span
									className={`block h-[85%] w-[85%] [perspective:400px] ${
										state.lastFlipped.includes(square) ? "animate-pop" : ""
									}`}
								>
									<span className="flip" data-face={cell === 0 ? "front" : "back"}>
										<span className="flip-face rounded-full bg-slate-900 shadow-md" />
										<span className="flip-face flip-face-back rounded-full bg-white shadow-md" />
									</span>
								</span>
							)}
						</button>
					);
				})}
			</div>
			<p className="mt-3 text-center text-sm opacity-70">
				You are {seat === 0 ? "black" : "white"}. Squares you can play are outlined.
			</p>
		</div>
	);
}

// --- Code Breaker ---------------------------------------------------------

export function CodeBreakerBoard({ state, seat, onMove }: BoardProps<CodeBreakerState>) {
	const myTurn = state.turn === seat && !state.result;
	const [draft, setDraft] = useState<number[]>(Array(CODE_LENGTH).fill(0));
	const [slot, setSlot] = useState(0);

	const setColour = (colour: number) => {
		const next = [...draft];
		next[slot] = colour;
		setDraft(next);
		setSlot((slot + 1) % CODE_LENGTH);
	};

	return (
		<div className="board-frame board-lg text-center">
			<div className="mb-4 flex justify-center gap-2">
				{draft.map((colour, index) => (
					<button
						key={index}
						type="button"
						aria-label={`Slot ${index + 1}`}
						aria-pressed={slot === index}
						onClick={() => setSlot(index)}
						className={`h-14 w-14 rounded-2xl text-3xl shadow-md transition ${
							slot === index ? "scale-110 bg-indigo-600/90" : "bg-white/70 dark:bg-white/10"
						}`}
					>
						{CODE_COLOURS[colour]}
					</button>
				))}
			</div>

			<div className="mb-4 flex justify-center gap-2">
				{CODE_COLOURS.map((colour, index) => (
					<button
						key={colour}
						type="button"
						aria-label={`Colour ${index + 1}`}
						onClick={() => setColour(index)}
						className="rounded-xl bg-white/70 px-2 py-1 text-2xl transition hover:scale-110 dark:bg-white/10"
					>
						{colour}
					</button>
				))}
			</div>

			<Button size="lg" disabled={!myTurn} onClick={() => onMove({ code: draft })}>
				{myTurn ? "Try this code" : "Their turn…"}
			</Button>

			<ul className="mt-6 space-y-2">
				{[...state.guesses].reverse().map((guess, index) => (
					<li
						key={`${guess.code.join("")}-${index}`}
						className="flex items-center justify-between rounded-2xl bg-white/60 px-4 py-2 dark:bg-white/10"
					>
						<span className="text-2xl">{guess.code.map((c) => CODE_COLOURS[c]).join(" ")}</span>
						<span className="text-sm font-bold">
							{guess.seat === seat ? "you" : "them"} · {guess.exact} exact · {guess.near} near
						</span>
					</li>
				))}
			</ul>
		</div>
	);
}

// --- Sliding Puzzle Race --------------------------------------------------

export function SlidingBoard({ state, seat, onMove }: BoardProps<SlidingState>) {
	const board = state.boards[seat];
	const them: PlayerIndex = seat === 0 ? 1 : 0;
	const blank = board.indexOf(0);

	const canSlide = (index: number) => {
		const col = index % SLIDE_SIZE;
		const row = Math.floor(index / SLIDE_SIZE);
		const bc = blank % SLIDE_SIZE;
		const br = Math.floor(blank / SLIDE_SIZE);
		return Math.abs(col - bc) + Math.abs(row - br) === 1;
	};

	return (
		<div className="board-frame board-sm text-center">
			<p className="mb-3 text-lg font-semibold">
				Your moves: {state.moves[seat]} · theirs: {state.moves[them]}
			</p>
			<div
				className="mx-auto grid aspect-square gap-2 rounded-2xl bg-white/60 p-2 shadow-xl dark:bg-white/10"
				style={{ gridTemplateColumns: `repeat(${SLIDE_SIZE}, minmax(0, 1fr))` }}
			>
				{board.map((tile, index) => (
					<button
						key={index}
						type="button"
						disabled={tile === 0 || !canSlide(index) || state.result !== null}
						aria-label={tile === 0 ? "The gap" : `Tile ${tile}`}
						onClick={() => onMove({ tile: index })}
						className={`flex items-center justify-center rounded-xl text-4xl font-black transition ${
							tile === 0
								? "bg-transparent"
								: "bg-indigo-500 text-white shadow-md enabled:hover:scale-105"
						}`}
					>
						{tile === 0 ? "" : tile}
					</button>
				))}
			</div>
			<p className="mt-3 text-sm opacity-70">Put 1 to 8 back in order. No turns — just go!</p>
		</div>
	);
}

// --- Pit Row --------------------------------------------------------------

export function PitRowBoard({ state, seat, onMove }: BoardProps<PitRowState>) {
	const myTurn = state.turn === seat && !state.result;
	const mine = seat === 0 ? [0, 1, 2, 3, 4, 5] : [7, 8, 9, 10, 11, 12];
	const theirs = seat === 0 ? [12, 11, 10, 9, 8, 7] : [5, 4, 3, 2, 1, 0];

	return (
		<div className="board-frame board-xl">
			<div className="flex items-center gap-2 rounded-3xl bg-amber-200/70 p-3 shadow-xl dark:bg-amber-900/30">
				<Store count={state.board[STORE[seat === 0 ? 1 : 0]]} label="Theirs" />

				<div className="flex-1">
					<div className="mb-2 grid grid-cols-6 gap-2">
						{theirs.map((pit) => (
							<Pit key={pit} count={state.board[pit]} />
						))}
					</div>
					<div className="grid grid-cols-6 gap-2">
						{mine.map((pit) => (
							<Pit
								key={pit}
								count={state.board[pit]}
								onClick={myTurn && state.board[pit] > 0 ? () => onMove({ pit }) : undefined}
								highlight={pit === state.lastPit}
							/>
						))}
					</div>
				</div>

				<Store count={state.board[STORE[seat]]} label="Yours" />
			</div>

			<p className="mt-3 text-center font-semibold">
				{state.lastNote ?? (myTurn ? "Pick one of your pits (the bottom row)" : "Their turn…")}
			</p>
			<p className="mt-1 text-center text-sm opacity-70">
				{PITS_PER_SIDE} pits each — your store is on the right.
			</p>
		</div>
	);
}

function Pit({
	count,
	onClick,
	highlight,
}: {
	count: number;
	onClick?: () => void;
	highlight?: boolean;
}) {
	const content = (
		<>
			<span className="text-xl font-black">{count}</span>
			<span className="text-xs" aria-hidden="true">
				{"🫘".repeat(Math.min(count, 4))}
			</span>
		</>
	);
	return onClick ? (
		<button
			type="button"
			onClick={onClick}
			aria-label={`Pit with ${count} stones`}
			className={`flex aspect-square flex-col items-center justify-center rounded-full bg-white/80 shadow-inner transition hover:scale-105 dark:bg-white/20 ${
				highlight ? "ring-4 ring-amber-400" : ""
			}`}
		>
			{content}
		</button>
	) : (
		<span className="flex aspect-square flex-col items-center justify-center rounded-full bg-white/50 shadow-inner dark:bg-white/10">
			{content}
		</span>
	);
}

function Store({ count, label }: { count: number; label: string }) {
	return (
		<span className="flex h-32 w-14 flex-col items-center justify-center rounded-full bg-white/80 shadow-inner dark:bg-white/20">
			<span className="text-2xl font-black">{count}</span>
			<span className="text-[10px] font-bold opacity-70">{label}</span>
		</span>
	);
}
